import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import {
  CommunityInput,
  CommunityInputCulture,
  CommunityInputError,
  CommunityInputImage,
  CommunityInputInstruction,
  CommunityInputNamed,
  CommunityInputSubject,
} from './community-input.model';

const JSON_API_MEDIA_TYPE = 'application/vnd.api+json';

interface ResourceObject {
  type: string;
  id: string;
  attributes?: Record<string, any>;
  meta?: Record<string, any>;
}

/**
 * Community inputs from the MSC backend — the Ideas plan, decision 3.32, and section 11.4.
 * JSON:API. Every route answers "not found" to a reader holding none of the input's roles.
 */
@Injectable({ providedIn: 'root' })
export class CommunityInputService {
  private http = inject(HttpClient);
  private readonly headers = new HttpHeaders({ Accept: JSON_API_MEDIA_TYPE, 'Content-Type': JSON_API_MEDIA_TYPE });

  /**
   * `from` names the idea the reader came from: it decides the cultures an idea's input offers.
   * `delivery` names which of the reader's own deliveries from the input to serve as `myDelivery`,
   * for a member given several by staff; without it, the server serves their latest.
   */
  getOne(slug: string, from: string | null = null, delivery: number | null = null): Observable<CommunityInput> {
    const params: Record<string, string> = {
      ...(from ? { from } : {}),
      ...(delivery !== null ? { delivery: String(delivery) } : {}),
    };

    return this.http
      .get<{ data: ResourceObject }>('api/v1/community-inputs/' + encodeURIComponent(slug), { headers: this.headers, params })
      .pipe(
        map((document) => CommunityInputService.normalise(document.data)),
        catchError((error) => this.fail(error)),
      );
  }

  /**
   * Commit: the delivery is created at once, or the reader's existing one returned. `via` names
   * the idea the reader came from; the delivery records it when the input serves that idea.
   * `culture` is the culture the reader chose to write about, when more than one is offered.
   */
  take(slug: string, via: string | null = null, culture: string | null = null): Observable<CommunityInput> {
    const body: Record<string, string> = {};

    if (via) {
      body['via'] = via;
    }

    if (culture) {
      body['culture'] = culture;
    }

    return this.http
      .post<{ data: ResourceObject }>('api/v1/community-inputs/' + encodeURIComponent(slug) + '/take', body, { headers: this.headers })
      .pipe(
        map((document) => CommunityInputService.normalise(document.data)),
        catchError((error) => this.fail(error)),
      );
  }

  /** Hand in a feature review: the feedback is in the conversation, or went as the instructions say. */
  submitReview(slug: string, deliveryId: number): Observable<CommunityInput> {
    return this.http
      .post<{ data: ResourceObject }>(
        'api/v1/community-inputs/' + encodeURIComponent(slug) + '/deliveries/' + deliveryId + '/submit',
        {},
        { headers: this.headers },
      )
      .pipe(
        map((document) => CommunityInputService.normalise(document.data)),
        catchError((error) => this.fail(error)),
      );
  }

  static image(raw: any): CommunityInputImage | null {
    return raw?.path ? { disk: String(raw.disk), path: String(raw.path), altText: raw.alt_text ?? null } : null;
  }

  static normalise(resource: ResourceObject): CommunityInput {
    const a = resource.attributes ?? {};
    const named = (list: any): CommunityInputNamed[] =>
      (Array.isArray(list) ? list : []).map((item) => ({ slug: String(item?.slug ?? ''), name: item?.name ?? null }));
    const instructions = (list: any): CommunityInputInstruction[] =>
      (Array.isArray(list) ? list : []).map((item) => ({ s: item?.s ?? null, m: item?.m ?? null })).filter((item) => item.s || item.m);
    const delivery = a['my_delivery'];
    const culture = (item: any): CommunityInputCulture => ({ slug: String(item?.slug ?? ''), name: item?.name ?? null, flag: item?.flag ?? null });
    const subject = (item: any): CommunityInputSubject | null =>
      item?.slug ? { slug: String(item.slug), title: item.title ?? null, question: item.question ?? null } : null;

    return {
      slug: a['slug'],
      name: a['name'] ?? null,
      status: a['status'] === 'closed' ? 'closed' : 'open',
      offered: !!a['offered'],
      kind: a['kind'] === 'insight_writing' || a['kind'] === 'video_recording' ? a['kind'] : 'feature_review',
      task: {
        slug: a['task']?.['slug'] ?? '',
        name: a['task']?.['name'] ?? null,
        description: a['task']?.['description'] ?? null,
        longDescription: a['task']?.['long_description'] ?? null,
        creditsMin: a['task']?.['credits_min'] ?? null,
        creditsMax: a['task']?.['credits_max'] ?? null,
      },
      whatsInvolved: a['whats_involved'] || null,
      instructions: {
        general: instructions(a['instructions']?.['general']),
        particular: instructions(a['instructions']?.['particular']),
        videos: (Array.isArray(a['instructions']?.['videos']) ? a['instructions']['videos'] : []).map((video: any) => ({
          name: video?.name ?? null,
          host: video?.host ?? null,
          hostIdentifier: video?.host_identifier ?? null,
          pageUrl: video?.page_url ?? null,
        })),
      },
      ideas: named(a['ideas']),
      deliverIn: a['deliver_in']?.['iso']
        ? { iso: String(a['deliver_in']['iso']), name: a['deliver_in']['name'] ?? null, flag: a['deliver_in']['flag'] ?? null }
        : null,
      images: {
        thumbnail: CommunityInputService.image(a['images']?.['thumbnail']),
        hero: CommunityInputService.image(a['images']?.['hero']),
      },
      cultureSource: ['listed', 'volunteer', 'idea'].includes(a['culture_source']) ? a['culture_source'] : null,
      from: a['from'] ?? null,
      candidateCultures: (Array.isArray(a['candidate_cultures']) ? a['candidate_cultures'] : []).map((item: any) => ({
        ...culture(item),
        likelySubject: subject(item?.likely_subject),
      })),
      can: { take: !!a['can']?.['take'] },
      alternative: typeof a['alternative'] === 'string' && a['alternative'] ? a['alternative'] : null,
      myDelivery: delivery
        ? {
            id: Number(delivery['id']),
            state: delivery['state'] ?? null,
            states: Array.isArray(delivery['states']) ? delivery['states'] : [],
            submittedAt: delivery['submitted_at'] ? new Date(delivery['submitted_at']) : null,
            via: delivery['via'] ?? null,
            insightSlug: delivery['insight_slug'] ?? null,
            sourceLang: delivery['source_lang'] ?? null,
            targetLang: delivery['target_lang'] ?? null,
            subject: subject(delivery['subject']),
            culture: delivery['culture']?.['slug'] ? culture(delivery['culture']) : null,
          }
        : null,
      videoSubmission: a['video_submission']?.['code']
        ? {
            code: String(a['video_submission']['code']),
            account: a['video_submission']['account'] ?? '',
            whatsapp: a['video_submission']['whatsapp'] ?? null,
            link: a['video_submission']['link'] ?? null,
            qr: typeof a['video_submission']['qr'] === 'string' && a['video_submission']['qr'].startsWith('data:image/svg+xml;base64,') ? a['video_submission']['qr'] : null,
          }
        : null,
    };
  }

  private fail(error: HttpErrorResponse): Observable<never> {
    const status = error?.status ?? 0;
    // The shared keys `ErrorService` uses; nothing here is Inputs' own.
    const translationKey =
      status === 404 ? 'common.not_found'
      : status === 403 ? 'error.not_allowed'
      : status === 409 ? 'inputs.no_longer_offered'
      : status === 422 ? 'error.data_invalid'
      : status === 401 ? 'authentication.not_authenticated'
      : 'error.not_done_try_again';

    const failure: CommunityInputError = { status, translationKey };

    return throwError(() => failure);
  }
}
